/**
 * Set (or replace) the account password — the AD-16 action and its AD-17
 * confirm (login-password-passkey-ui-plan §2.2.9 · §2.2.10).
 *
 * A REAL MODAL inside a disclosure that is deliberately not one. `account-menu`
 * builds no focus trap on purpose; this dialog must, because it is the one
 * destructive control in that panel and a Tab that escapes it lands on
 * `Sign out`. The two Tab models never run at once: the panel unmounts its own
 * children while this is open, so the only focusable controls IN THE PANEL are
 * the ones inside the trap. The rest of the page is still rendered, so
 * `useFocusContainment` is what stops focus resting outside and makes
 * `aria-modal` true for focus. The background is NOT pointer-inert — that needs
 * a portal and `inert`, which this dialog deliberately does not build: it draws
 * no backdrop and sits inline in the panel.
 *
 * Six properties this file exists to hold, none of them visible to the type
 * system:
 *
 *  1. **Escape is consumed HERE.** The listener is on the dialog node, plus a
 *     `document` fallback for a press that lands while focus is outside it —
 *     AccountMenu's own Escape handler stands down while the confirm is open,
 *     so without the fallback Escape is dead on both layers. See `useDialogKeys`.
 *  2. **A 200 is not a success.** `handleSetPassword` answers
 *     `okEnvelope({ set: true })`; deciding from `res.ok` alone would report a
 *     replaced credential for any other body the route could send.
 *  3. **A 401 here is unambiguous, so it does NOT refresh.** See `classifyError`.
 *  4. **The typed value is cleared with `flushSync`.** The clear and the
 *     dialog's unmount are batched into one commit, so without it the input is
 *     removed without ever rendering the empty value and the plaintext survives
 *     in the detached node's reflected `value` attribute.
 *  5. **Submit state lives on `SetPassword`, not the dialog.** Cancel unmounts
 *     the confirm; the POST is not aborted. A generation counter supersedes a
 *     dismissed attempt so a late 200 cannot announce success as THIS attempt,
 *     and `inFlight` survives so a reopen cannot start a second write.
 *  6. **A superseded save is reported, not discarded.** Cancel does not revoke a
 *     request already sent, so a 200 landing after it means the credential WAS
 *     replaced. Staying silent would tell the user their Cancel prevented a
 *     replacement it did not prevent.
 *
 * It reads no session state at all: `useAuth` is never called, which is what
 * lets the component mount bare and keeps point 3 a property rather than a
 * promise.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type JSX,
  type RefObject,
} from 'react';

import type { AppConfig } from '@/config';

import { focusIsUnclaimed } from './focus-ownership';
import { clearPasswordSync, describedByFor, isPasswordFieldError } from './password-field';
import styles from './set-password.module.css';
import { messageFor, useSetPasswordSubmit, type SetPasswordError } from './use-set-password-submit';

const TRIGGER_LABEL = 'Set password';
const FIELD_LABEL = 'New password';
/* The replace half of AD-16 stated up front, together with the reassurance that
   the recovery credential survives it — a set-replace with no such statement
   reads as "this is now my only way in". */
const HINT_COPY = 'Replaces any existing password. Your passkey still works.';
const CANCEL_LABEL = 'Cancel';
const SAVE_LABEL = 'Save password';
const SAVING_LABEL = 'Saving…';
const SAVING_STATUS = 'Saving password…';
const DONE_COPY = 'Password set.';
/* The outcome of an attempt the user cancelled AFTER the request went out. It
   is deliberately not DONE_COPY: the save the user asked to abandon is not a
   confirmation they asked for, but the credential did change and silence would
   be a false statement about the account's most important secret. */
const REPLACED_COPY = 'Password replaced — the save had already been sent when you cancelled.';

/*
 * The dialog's own controls, collected fresh on every Tab.
 *
 * Nothing in this dialog ever takes the `disabled` ATTRIBUTE — the busy confirm
 * is `aria-disabled` precisely so it keeps its place here — so the population is
 * in fact stable today. The selector stays a selector, and stays filtered,
 * because the alternative is a list captured at mount that would silently start
 * lying the first time a control is genuinely disabled: it would send the wrap
 * to a node focus cannot reach. The dialog's content is fixed and local — one
 * input and two buttons — so no `tabindex="-1"` filter is owed; adding such a
 * node here means extending this.
 */
const FOCUSABLE_SELECTOR = 'button:not([disabled]), input:not([disabled])';

interface DialogForm {
  readonly password: string;
  readonly busy: boolean;
  readonly error: SetPasswordError | null;
  readonly handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

/** Everything the dialog's markup needs, plus the shared dismissal path. */
function useSetPasswordForm(
  config: AppConfig,
  onClose: (saved: boolean) => void,
  onReplaced: () => void,
): {
  readonly form: DialogForm;
  readonly dismiss: () => void;
  readonly resetError: () => void;
} {
  const [password, setPassword] = useState('');
  const onSaved = useCallback(() => {
    onClose(true);
  }, [onClose]);
  const { busy, error, setError, submit, supersede } = useSetPasswordSubmit({
    config,
    credential: { password, setPassword },
    onSaved,
    onReplaced,
  });

  /* Cancel and Escape share one path. It spends no NEW request, but it does not
     revoke one already sent — `supersede` only invalidates the generation, so a
     late 200 is reported as a replacement rather than as this attempt's
     success (see property 6 in the file header). */
  const dismiss = useCallback(() => {
    supersede();
    clearPasswordSync(setPassword);
    onClose(false);
  }, [onClose, supersede]);

  /* The submit state outlives the dialog, so a reopen would otherwise mount
     showing the previous attempt's alert over an empty field — and, for a field
     error, `aria-invalid` on a value the user has not typed yet. */
  const resetError = useCallback(() => {
    setError(null);
  }, [setError]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      /* Native submit, prevented: Enter keeps working without a key handler,
         which is the whole reason this is a <form> (§2.5.1). */
      event.preventDefault();
      void submit();
    },
    [submit],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
      setError(null);
    },
    [setError],
  );

  return { form: { password, busy, error, handleChange, handleSubmit }, dismiss, resetError };
}

/**
 * Keep Tab inside the dialog.
 *
 * Focusables are read at KEYDOWN time, never captured at mount: the confirm is
 * disabled while a save is in flight, so the last control changes under us.
 * A middle Tab is left alone deliberately — a trap that preventDefaults every
 * Tab re-implements the browser's traversal and reorders the moment the DOM
 * does.
 *
 * Position comes from `event.target`, not from `document.activeElement`. In a
 * browser they are the same node — a keydown is dispatched at the focused
 * element — and the target is the one of the two that is a property of THIS
 * event. `activeElement` is ambient: it answers for whatever happened last,
 * which for a synthetic dispatch (and for a key pressed while a control is
 * being replaced mid-render) is not the control the key was pressed on.
 */
function containTab(event: KeyboardEvent, dialog: HTMLElement): void {
  const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  /* Cross-module guarantee, not a local one: the markup below always renders at
     least one enabled control, but the selector above is what decides. */
  if (first === undefined || last === undefined) return;
  const target = event.target;
  const position = target instanceof HTMLElement ? focusables.indexOf(target) : -1;
  if (position === -1) {
    /* The key arrived from no control of ours — in practice focus has fallen
       outside the dialog, which a browser never delivers here. The trap
       reclaims it rather than letting the Tab continue into the page, and
       honours the direction asked for: a Shift+Tab that answered by jumping
       FORWARD to the first control would reverse the user's request. */
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  if (event.shiftKey && position === 0) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (!event.shiftKey && position === focusables.length - 1) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Keys, on the DIALOG ELEMENT — plus a narrow `document` fallback for Escape.
 *
 * The node listener owns every key pressed inside the dialog. `preventDefault()`
 * on Escape stays: it suppresses the native Escape behaviour, and AccountMenu's
 * `defaultPrevented` guard is a second reason it is correct — though no longer
 * the operative one, since the panel now stands down for the whole lifetime of
 * the confirm via `confirmOpenRef`. An `onKeyDown` prop on this non-interactive
 * element is what `jsx-a11y/no-noninteractive-element-interactions` forbids.
 *
 * The fallback exists because a node listener answers only for focus that is
 * INSIDE the node. `useFocusContainment` pulls focus back when it lands on
 * another element, but a click on non-focusable background text fires no
 * `focusin` and leaves `activeElement` on `<body>` — and AccountMenu's own
 * Escape handler has stood down. Without this arm Escape is dead on both
 * layers and the confirm is closable only by pointer.
 */
function useDialogKeys(dialogRef: RefObject<HTMLDivElement>, onDismiss: () => void): void {
  useEffect(() => {
    const node = dialogRef.current;
    if (node === null) return;
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onDismiss();
        return;
      }
      if (event.key === 'Tab') containTab(event, node);
    };
    const handleDocumentKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      /* Inside the dialog the node listener above already answered. */
      const target = event.target;
      if (target instanceof Node && node.contains(target)) return;
      event.preventDefault();
      onDismiss();
    };
    node.addEventListener('keydown', handleKey);
    document.addEventListener('keydown', handleDocumentKey);
    return () => {
      node.removeEventListener('keydown', handleKey);
      document.removeEventListener('keydown', handleDocumentKey);
    };
  }, [dialogRef, onDismiss]);
}

/**
 * Focus cannot rest outside the dialog while it is open.
 *
 * `aria-modal="true"` is a PROMISE about the background, and `containTab` alone
 * cannot keep it: that handler only ever runs for a Tab pressed on a control
 * the dialog already owns. A pointer press on anything outside the panel moves
 * focus out without a key being pressed at all, and the panel's own dismissals
 * have stood down, so nothing closes and nothing reclaims — from there the
 * dialog is reachable only by tabbing back in.
 *
 * This does NOT make the background pointer-inert; a click still activates what
 * it hit. Delivering that needs a portal and `inert`, which this dialog
 * deliberately does not build (it draws no backdrop and sits inline in the
 * panel). What it does deliver is the focus half of the promise.
 */
function useFocusContainment(dialogRef: RefObject<HTMLDivElement>): void {
  useEffect(() => {
    const node = dialogRef.current;
    if (node === null) return;
    const handleFocusIn = (event: FocusEvent): void => {
      const target = event.target;
      if (target instanceof Node && node.contains(target)) return;
      node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [dialogRef]);
}

/** Initial focus on the field, per APG: the dialog exists to collect it. */
function useInitialFieldFocus(fieldRef: RefObject<HTMLInputElement>): void {
  useEffect(() => {
    fieldRef.current?.focus();
  }, [fieldRef]);
}

/*
 * There is no `useFieldFocusDuringSubmit` here any more, and that is the point.
 *
 * It existed to catch focus that `disabled={busy}` threw on the floor: the
 * browser blurs a disabled control to `document.body`, the dialog's key
 * listener is on the dialog NODE, and a Tab or Escape targeted at body reaches
 * neither it nor anything else. Moving focus to the field papered over that,
 * at the cost of yanking focus on every busy transition — away from a control
 * the user had deliberately activated, reopening the virtual keyboard on touch,
 * and re-announcing the field to a screen reader mid-request.
 *
 * `aria-disabled` on the confirm removes the cause instead. Save keeps focus,
 * keeps its place in the tab order and stays in the a11y tree, so its
 * SAVE → SAVING label change is itself the WCAG 4.1.3 announcement, the key
 * listener stays reachable, and nothing has to be reclaimed. The double-submit
 * guard was never the `disabled` attribute anyway — it is the `inFlight` ref in
 * `runSetPasswordSubmit`, which is what makes a bypassed button harmless.
 */

/**
 * Return focus to the trigger once the dialog is gone.
 *
 * The trigger has just REMOUNTED in the same commit — it is the dialog's slot —
 * so the ref is current by the time this runs. `wasOpen` keeps the close arm
 * from firing on mount, and `focusIsUnclaimed` keeps it from stealing focus a
 * user deliberately moved elsewhere while the dialog was closing.
 */
function useRestoreTriggerFocus(open: boolean, triggerRef: RefObject<HTMLButtonElement>): void {
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!open && wasOpen.current && focusIsUnclaimed()) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open, triggerRef]);
}

/**
 * What the surface has to say about a settled write, if anything.
 *
 * `'replaced-after-dismiss'` is a separate arm rather than a second `'set'`
 * because the two are different statements: one confirms what the user asked
 * for, the other reports something that happened despite their Cancel.
 */
type SetOutcome = 'set' | 'replaced-after-dismiss';

/** Pending copy is polite and hidden; a settled outcome is polite and visible. */
function noticeFor(outcome: SetOutcome | null, busy: boolean): string {
  if (outcome === 'set') return DONE_COPY;
  if (outcome === 'replaced-after-dismiss') return REPLACED_COPY;
  return busy ? SAVING_STATUS : '';
}

function noticeClass(outcome: SetOutcome | null): string | undefined {
  if (outcome === 'set') return styles['success'];
  if (outcome === 'replaced-after-dismiss') return styles['notice'];
  return 'visually-hidden';
}

export interface SetPasswordProps {
  readonly config: AppConfig;
  /**
   * Called with the confirm dialog's open state.
   *
   * `AccountPanel` unmounts its other children while this is true — that, and
   * not an `aria-hidden` attribute, is how AD-17's "background inert" is
   * delivered: `aria-hidden` over still-focusable controls is an
   * `aria-hidden-focus` violation, while not rendering them has no such cost.
   */
  readonly onOpenChange?: (open: boolean) => void;
}

export function SetPassword({ config, onOpenChange }: SetPasswordProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<SetOutcome | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useRestoreTriggerFocus(open, triggerRef);

  const handleClose = useCallback(
    (saved: boolean) => {
      if (saved) setOutcome('set');
      setOpen(false);
      onOpenChange?.(false);
    },
    [onOpenChange],
  );
  const handleReplaced = useCallback(() => {
    setOutcome('replaced-after-dismiss');
  }, []);
  /* Owned here so Cancel can unmount the dialog without resetting inFlight. */
  const { form, dismiss, resetError } = useSetPasswordForm(config, handleClose, handleReplaced);

  const handleOpen = useCallback(() => {
    /* A new attempt starts from no outcome and no alert: the previous one
       described a replacement that is no longer the subject, and its error was
       a statement about a value this field no longer holds. */
    setOutcome(null);
    resetError();
    setOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange, resetError]);

  return (
    <div className={styles['root']}>
      {/* The dialog TAKES the trigger's slot rather than joining it: two
          controls named `Set password` in one panel is an ambiguity for every
          user, and for every query that names one. */}
      {open ? (
        <SetPasswordDialog form={form} onDismiss={dismiss} />
      ) : (
        <button ref={triggerRef} type="button" className={styles['trigger']} onClick={handleOpen}>
          {TRIGGER_LABEL}
        </button>
      )}
      {/*
       * EXACTLY ONE polite region for this whole surface — pending copy and
       * settled copy both, which is what makes the claim true rather than
       * merely stated. It outlives the dialog, so the success announcement is
       * not queued from a node that is unmounting in the same commit, and the
       * count `chrome-a11y.test.tsx` pins stays one per panel action.
       *
       * An injected live region registers with assistive tech too late to
       * announce its own first message, so this one is mounted always and
       * silent until there is something to say. It is `visually-hidden` while
       * pending or empty and VISIBLE for a settled outcome — one node changing
       * class, not a hidden node plus a visible twin — so a sighted user gets
       * the only trace the set-replace leaves without the sentence appearing
       * twice in the accessible tree.
       */}
      <p className={noticeClass(outcome)} role="status">
        {noticeFor(outcome, form.busy)}
      </p>
    </div>
  );
}

interface SetPasswordDialogProps {
  readonly form: DialogForm;
  readonly onDismiss: () => void;
}

function SetPasswordDialog({ form, onDismiss }: SetPasswordDialogProps): JSX.Element {
  const titleId = useId();
  const hintId = useId();
  const errorId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement>(null);
  useInitialFieldFocus(fieldRef);
  useDialogKeys(dialogRef, onDismiss);
  useFocusContainment(dialogRef);

  return (
    <div
      ref={dialogRef}
      className={styles['dialog']}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      /* No `aria-describedby` here. The hint is the FIELD's description, and
         `useInitialFieldFocus` moves focus to that field in the same commit the
         dialog mounts — naming it on both nodes reads the same sentence twice
         in a row. axe cannot see this: it checks only that the id resolves. */
      /* The stable query hook, for the reason `data-account-panel` exists: the
         node carries no semantic beyond its role, and a spec must not bind to a
         CSS-module class name. */
      data-set-password-dialog=""
    >
      {/* A <p>, not an <h*>: the disclosure contributes no heading to the
          document outline, so nothing here can raise `heading-order`. */}
      <p id={titleId} className={styles['title']}>
        {TRIGGER_LABEL}
      </p>
      <p id={hintId} className={styles['hint']}>
        {HINT_COPY}
      </p>
      <SetPasswordFields
        {...form}
        ids={{ hintId, errorId }}
        fieldRef={fieldRef}
        onCancel={onDismiss}
      />
    </div>
  );
}

interface SetPasswordFieldsProps extends DialogForm {
  readonly ids: { readonly hintId: string; readonly errorId: string };
  readonly fieldRef: RefObject<HTMLInputElement>;
  readonly onCancel: () => void;
}

/** Presentation only — every decision above it, nothing but markup here. */
function SetPasswordFields(props: SetPasswordFieldsProps): JSX.Element {
  const { ids, fieldRef, onCancel, password, busy, error, handleChange, handleSubmit } = props;
  /* The <form> is unnamed on purpose: a named one is exposed as a `form`
     LANDMARK, and this would be a landmark named `Set password` inside a dialog
     named `Set password`. The dialog already carries the name. */
  return (
    <form className={styles['form']} onSubmit={handleSubmit}>
      <label className={styles['label']}>
        {FIELD_LABEL}
        <input
          ref={fieldRef}
          className={styles['input']}
          type="password"
          /* `new-password`, never `current-password`: the latter makes a
             manager offer the SAVED credential as its own replacement. */
          autoComplete="new-password"
          value={password}
          onChange={handleChange}
          {...(error !== null && isPasswordFieldError(error) ? { 'aria-invalid': true } : {})}
          aria-describedby={describedByFor(ids.hintId, error !== null, ids.errorId)}
        />
      </label>
      {/* Mounted ALWAYS, contents conditional. A `role="alert"` node that is
          itself injected is a known announcement miss for some browser/SR
          pairs, and the id lives on the REGION so `aria-describedby` can never
          dangle no matter which errors come and go. */}
      <div id={ids.errorId} role="alert">
        {error !== null ? <p className={styles['error']}>{messageFor(error)}</p> : null}
      </div>
      <div className={styles['actions']}>
        {/* Never disabled: a save that hangs must not trap the user inside the
            trap this dialog installs. */}
        <button type="button" className={styles['cancel']} onClick={onCancel}>
          {CANCEL_LABEL}
        </button>
        {/* `aria-disabled`, never `disabled`. A disabled button is blurred to
            `document.body` and dropped from the a11y tree, which loses both the
            user's focus and the announcement of this very label change; this
            way Save keeps focus, keeps the dialog's key listener reachable, and
            announces itself. The write is guarded by the `inFlight` ref, so a
            submit that gets through here is a no-op rather than a second POST. */}
        <button type="submit" className={styles['confirm']} aria-disabled={busy}>
          {busy ? SAVING_LABEL : SAVE_LABEL}
        </button>
      </div>
      {/* WCAG 4.1.3 — a disabled button is blurred and dropped from the a11y
          tree, so the label change above announces nothing — is answered by the
          ONE polite region in `SetPassword`, which carries this surface's
          pending copy as well as its settled copy. A second region here would
          compete with it for announcements and, on the success path, would be
          torn down in the very commit the other one speaks. */}
    </form>
  );
}
